import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { storage } from './storage';
import { hasPlugin, isNative } from './native';

export type HapticEvent = 'click' | 'confirm' | 'invalid' | 'merge' | 'impact' | 'power' | 'ko';

const SETTINGS_KEY = '2k48.haptics.v1';
const nativeAvailable = isNative && hasPlugin('Haptics');

export const HAPTIC_PATTERNS: Readonly<Record<HapticEvent, number | readonly number[]>> = {
  click: 10,
  confirm: [20, 30, 30],
  invalid: [35, 45, 35],
  merge: 18,
  impact: 55,
  power: [25, 25, 65],
  ko: [80, 45, 110],
};

class HapticManager {
  private enabled = true;
  private initialised = false;

  async init(): Promise<void> {
    if (this.initialised) return;
    this.enabled = await storage.getJson<boolean>(SETTINGS_KEY, true);
    this.initialised = true;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * `persist: false` takes effect immediately without writing to disk, so the
   * settings screen can let the player feel the change before committing it.
   */
  setEnabled(enabled: boolean, { persist = true }: { persist?: boolean } = {}): void {
    this.initialised = true;
    this.enabled = enabled;
    if (persist) this.persistSettings();
    if (!enabled && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(0);
    }
  }

  /** Write whatever is currently in effect. The commit half of a preview. */
  persistSettings(): void {
    void storage.setJson(SETTINGS_KEY, this.enabled);
  }

  trigger(event: HapticEvent): void {
    // Never buzz with defaults before the persisted opt-out has loaded.
    if (!this.initialised || !this.enabled) return;
    if (nativeAvailable) {
      this.triggerNative(event);
      return;
    }
    this.triggerWeb(event);
  }

  /** Backwards-compatible roles used by the current game controllers. */
  light(): void {
    this.trigger('click');
  }

  medium(): void {
    this.trigger('merge');
  }

  heavy(): void {
    this.trigger('impact');
  }

  warn(): void {
    this.trigger('invalid');
  }

  success(): void {
    this.trigger('confirm');
  }

  private triggerNative(event: HapticEvent): void {
    const effect =
      event === 'click'
        ? Haptics.impact({ style: ImpactStyle.Light })
        : event === 'merge'
          ? Haptics.impact({ style: ImpactStyle.Medium })
          : event === 'impact'
            ? Haptics.impact({ style: ImpactStyle.Heavy })
            : event === 'confirm'
              ? Haptics.notification({ type: NotificationType.Success })
              : event === 'invalid'
                ? Haptics.notification({ type: NotificationType.Warning })
                : Haptics.vibrate({ duration: event === 'ko' ? 140 : 85 });
    void effect.catch(() => {});
  }

  private triggerWeb(event: HapticEvent): void {
    if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
    try {
      navigator.vibrate([...asPattern(HAPTIC_PATTERNS[event])]);
    } catch {
      // Unsupported patterns and user-disabled vibration are harmless.
    }
  }
}

export function asPattern(pattern: number | readonly number[]): readonly number[] {
  return typeof pattern === 'number' ? [pattern] : pattern;
}

export const haptics = new HapticManager();
